import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, invalidateAdminCache } from '../api.js';

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const refreshGen = useRef(0);
  const cooldownRef = useRef(0);
  const intervalRef = useRef(null);

  function getCooldownSeconds() {
    const ts = sessionStorage.getItem('admin_last_refresh');
    if (!ts) return 0;
    const elapsed = Date.now() - parseInt(ts, 10);
    return Math.max(0, Math.ceil((15000 - elapsed) / 1000));
  }

  function startCooldownTimer() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const tick = () => {
      const remaining = getCooldownSeconds();
      cooldownRef.current = remaining;
      setCooldown(remaining);
      if (remaining <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    tick();
    intervalRef.current = setInterval(tick, 250);
  }

  function stopCooldownTimer() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    const remaining = getCooldownSeconds();
    if (remaining > 0) {
      cooldownRef.current = remaining;
      setCooldown(remaining);
      startCooldownTimer();
    }
    return () => stopCooldownTimer();
  }, []);

  async function handleRefresh() {
    const remaining = getCooldownSeconds();
    if (remaining > 0) return;
    const gen = ++refreshGen.current;
    console.log('[AdminDashboard] Refresh clicked (gen ' + gen + ')');
    invalidateAdminCache();
    setRefreshing(true);
    sessionStorage.setItem('admin_last_refresh', String(Date.now()));
    startCooldownTimer();
    const [subData, stuData] = await Promise.all([
      api.getSubmissions().catch(e => { console.error('[AdminDashboard] getSubmissions error:', e); return { submissions: [] }; }),
      api.getStudents().catch(e => { console.error('[AdminDashboard] getStudents error:', e); return { students: [] }; }),
    ]);
    if (gen !== refreshGen.current) {
      console.log('[AdminDashboard] Stale refresh cancelled (gen ' + gen + ')');
      setRefreshing(false);
      return;
    }
    console.log('[AdminDashboard] Refresh complete, submissions:', subData.submissions?.length, 'students:', stuData.students?.length);
    setSubmissions(subData.submissions || []);
    setStudents(stuData.students || []);
    setLoading(false);
    setRefreshing(false);
  }

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const [subData, stuData] = await Promise.all([
        api.getSubmissions().catch(() => ({ submissions: [] })),
        api.getStudents().catch(() => ({ students: [] })),
      ]);
      if (cancelled) return;
      setSubmissions(subData.submissions || []);
      setStudents(stuData.students || []);
      setLoading(false);
    }
    run();
    return () => { cancelled = true; };
  }, []);

  const totalStudents = students.length;
  const studentsWithSubmissions = submissions.filter(s => s.attempts?.length > 0).length;

  // Build per-student-per-unit rows for clickable drill-down
  const unitRows = [];
  for (const sub of submissions) {
    if (!sub.attempts || sub.attempts.length === 0) continue;
    const unitsByCode = {};
    for (const attempt of sub.attempts) {
      const uc = attempt.unitCode || 'Unknown';
      if (!unitsByCode[uc]) unitsByCode[uc] = [];
      unitsByCode[uc].push(attempt);
    }
    for (const [unitCode, attempts] of Object.entries(unitsByCode)) {
      unitRows.push({
        studentName: sub.name || sub.username,
        username: sub.username,
        optionalID: sub.optionalID || '',
        course: sub.course,
        unitCode,
        attemptCount: attempts.length,
        latestScore: attempts[attempts.length - 1]?.score?.percent,
        bestScore: Math.max(...attempts.map(a => a.score?.percent || 0), 0),
      });
    }
  }

  // Sort by latest submission timestamp descending, then limit to last 15
  unitRows.sort((a, b) => {
    const aSub = submissions.find(s => s.optionalID === a.optionalID && s.username === a.username);
    const bSub = submissions.find(s => s.optionalID === b.optionalID && s.username === b.username);
    if (!aSub?.attempts || !bSub?.attempts) return 0;
    const aLatest = Math.max(...aSub.attempts.map(a => new Date(a.submittedAt || 0).getTime()));
    const bLatest = Math.max(...bSub.attempts.map(a => new Date(a.submittedAt || 0).getTime()));
    return bLatest - aLatest;
  });

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#5b6776' }}>Loading dashboard...</div>;
  }

  function handleRowClick(username, optionalID, unitCode) {
    const optId = String(optionalID || '').trim();
    if (optId) {
      navigate(`/students/${username}/unit/${encodeURIComponent(unitCode)}?optionalID=${encodeURIComponent(optId)}`);
    } else {
      navigate(`/students/${username}/unit/${encodeURIComponent(unitCode)}`);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ marginTop: 0, marginBottom: 0 }}>Dashboard</h2>
        <button 
          onClick={handleRefresh} 
          disabled={refreshing || cooldown > 0}
          style={{
            background: refreshing ? '#e67e22' : 'transparent',
            color: (refreshing || cooldown > 0) ? '#9aa5b4' : 'var(--brand)',
            border: '1px solid var(--border)',
            padding: '8px 16px',
            borderRadius: 4,
            cursor: (refreshing || cooldown > 0) ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          {refreshing ? '\u231B Refreshing...' : cooldown > 0 ? `\u{21BB} Refresh Data (${cooldown}s)` : '\u{21BB} Refresh Data'}
        </button>
      </div>
      <p style={{ color: '#5b6776', fontSize: 14, marginBottom: 24 }}>Welcome to the IBMA Assessment Admin Portal.</p>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Students" value={totalStudents} color="#4a90d9" />
        <StatCard label="With Submissions" value={studentsWithSubmissions} color="#f39c12" />
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginBottom: 24, padding: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 16, marginBottom: 16 }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/import-students')} style={{ marginRight: 8, background: 'var(--brand)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>Import Students (Excel)</button>
          <button onClick={() => navigate('/students')} style={{ background: 'transparent', color: 'var(--brand)', border: '1px solid var(--border)', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>Manage Students</button>
        </div>
      </div>

      {/* Submission Overview */}
      <div className="card">
        <h3 style={{ marginTop: 0, fontSize: 16, marginBottom: 16 }}>Recent Submissions</h3>
        {unitRows.length === 0 ? (
          <p style={{ padding: 24, textAlign: 'center', color: '#5b6776' }}>No submissions found yet.</p>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid var(--border)`, textAlign: 'left' }}>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>User ID</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Course</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Unit</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'right' }}>Attempts</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'center' }}>Latest Result</th>
                </tr>
              </thead>
              <tbody>
                {unitRows.slice(0, 15).map((row, idx) => (
                  <tr 
                    key={`${row.optionalID || row.username}_${row.unitCode}_${idx}`}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.username, row.optionalID, row.unitCode)}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8f9fb'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ padding: '10px 12px', fontWeight: 500 }}>{row.optionalID || row.username}</td>
                    <td style={{ padding: '10px 12px' }}>{row.course}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--brand)' }}>{row.unitCode}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{row.attemptCount}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {row.latestScore != null ? (
                        row.latestScore === 100
                          ? <span style={{ color: '#2ecc71', fontWeight: 600 }}>Pass</span>
                          : <span style={{ color: '#e74c3c', fontWeight: 600 }}>{row.latestScore}%</span>
                      ) : '\u2014'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="card" style={{ padding: 20, borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 13, color: '#5b6776', marginTop: 4 }}>{label}</div>
    </div>
  );
}
